<script setup>
import { computed, ref, onMounted, onBeforeMount, onBeforeUnmount, watch } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { activateIntercom } from '@/lib/analytics';
import AppFooter from '@/components/layout/AppFooter';
import ConfirmModal from '@/components/modals/ConfirmModal';
import UserService from '@/services/UserService';
import { getSigninUrl } from '@/Utils/authRedirect';
import { identifyUser } from '@/lib/tracking';
import { clearAllPreviousCache } from './services/CacheManager';
import MainSidebar from './components/layout/MainSidebar.vue';

const route = useRoute()
const store = useStore()

// Computed
const isLoggedIn = computed(() => store.getters['Auth/isLoggedIn'])
const sidebarExpanded = computed(() => store.getters['Ui/sidebarExpanded'])
const user = computed(() => store.state.User)

// State
const appInitialized = ref(false)
const previousStreamId = ref(null)

// Watch - Handle route changes
watch(route, async (newRoute, oldRoute) => {
	// Don't re-initialize on route changes (already done in onBeforeMount)
	if (!appInitialized.value) {
		return;
	}
	
	// Clear stream-specific caches when navigating to a different stream
	const newStreamId = newRoute.params?.streamId || newRoute.params?.transcoderId
	const oldStreamId = oldRoute?.params?.streamId || oldRoute?.params?.transcoderId
	
	if (newStreamId && oldStreamId && newStreamId !== oldStreamId) {
		// Clear cached stream-specific data (metadata, scheduler config, etc.)
		// This ensures fresh data is fetched for the new stream
		store.commit('StreamCache/CLEAR_STREAM_CACHE', oldStreamId);
	}
	
	previousStreamId.value = newStreamId;
})

// Methods
const initializeApp = async () => {
	if (appInitialized.value) return;

	// Skip the authed shell bootstrap on fullScreen/auth routes (e.g. /app/login).
	// On an a_token landing, SignIn.vue's tryAutoLogin applies the token and does a
	// full-page redirect into the app; firing getStreams/loadUser here would race
	// the token, 401, and bounce back to login. Not flagging appInitialized so the
	// post-redirect dashboard boot still runs the real bootstrap.
	if (route.meta.fullScreen) return;

	try {
		// 1. Login/auth check
		await store.dispatch('Auth/login');
		
		// 2. Load user data (only once)
		await store.dispatch('User/loadUser');
		
		// 3. Load streams data (only once) - needed for most pages
		await store.dispatch('Streams/getStreams');

		// Regions, transcoders, and the team list are cached for the SPA session.
		// Do not block first paint on them — destination pages reuse isLoaded /
		// memberDetailsLoaded (or join the in-flight request).
		store.dispatch('Regions/getRegions', 'list');
		store.dispatch('Transcoders/getTranscoders');
		store.dispatch('User/getinfoMember');
		
		activateIntercom(user.value);
		appInitialized.value = true;
	} catch (error) {
		console.error('App initialization error:', error);
	}
}

const handleStorageChange = (event) => {
	if (event.key !== 'token') return

	const oldToken = event.oldValue
	const newToken = event.newValue

	// If token was removed in another tab, force logout in this tab
	if (oldToken && !newToken) {
		window.location.href = getSigninUrl()
		return
	}

	// If token changed (switched accounts), send user to their dashboard
	if (oldToken && newToken && oldToken !== newToken) {
		window.location.href = '/app/dashboard'
	}
}

// Hooks
onBeforeMount(async () => {
	clearAllPreviousCache()
	await initializeApp();
})
onMounted(() => {
	window.addEventListener('storage', handleStorageChange)
	enableFullStory();
	enableHeap()
})
onBeforeUnmount(() => {
	window.removeEventListener('storage', handleStorageChange)
})

// Methods
const authenticated = () => {
	return UserService.authenticated();
}
const navigateToBilling = () => {
	window.location.href = '/app/manage/billing';
}

const changeWinTitle = (viewTitle) => {
	let updatedTitle = 'Castr';
	if (viewTitle) {
		updatedTitle = viewTitle + ' - ' + updatedTitle;
	}
	document.title = updatedTitle.trim();
}

const enableFullStory = () => {
	setTimeout(() => {
		if (authenticated()) {
			const { name, cmsEntityId: amemberUserId, email } = UserService.getUser();
			!_.isEmpty(window.FS) && window.FS.identify(amemberUserId, {
				displayName: name,
				email: email,
				dashboard: 'v2'
			});
		}
	}, 2000);
}

const enableHeap = () => {
	setTimeout(() => {
		if (!authenticated()) return;
		const user = UserService.getUser();
		// PostHog: identify + set user/profile properties (plan, trial, etc.).
		identifyUser(user);
		if (window.heap && user) {
			window.heap.identify(user.email);
			window.heap.addUserProperties({ Name: user.name || 'Noname' });
		}
	}, 1000);
}

</script>

<template>
	<div class="main-wrapper flex flex-col md:flex-row">
		<MainSidebar v-if="!route.meta.fullScreen" />
		<!-- pb-20 md:pb-6: with the footer, keeps the last row of right-aligned
		     actions above the Intercom launcher's corner (48px + 20px offset) once
		     the page is scrolled to its end. -->
		<div class="flex flex-wrap justify-center mt-6 lg:mt-0 w-full translate-all self-start lg:min-h-screen"
			:class="route.meta.fullScreen
				? 'w-full'
				: [isLoggedIn ? 'main-content-wrapper' : 'container', sidebarExpanded ? 'md:max-w-[calc(100vw-260px)]' : 'md:max-w-[calc(100vw-64px)]', 'pb-20 md:pb-6']">
			<router-view :subscriptions="user && user.subscriptions" :products-features="user && user.aggregatedDefinition"
				@updateTitle="changeWinTitle" />
			<app-footer />
		</div>
	</div>
	<notifications group="error" classes="vue-notification error" style="top:10px;right:10px" />
	<notifications group="success" classes="vue-notification success" style="top:10px;right:10px" />
	<notifications group="info" classes="vue-notification info" style="top:10px;right:10px" />
	<div id="thumb-temp-loader" />
	<confirm-modal :modal-id="'billing-prompt'" message="Please settle pending subscription invoices before continue."
		ok-text="See Billing" cancel-text="Close" @modal-confirm="navigateToBilling()" />
</template>

<style>
#app {
	background-color: var(--c-dark-1);
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
}
</style>
